"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { toast } from "@/hooks/use-toast";
import { LanguageSkillsSection } from "@/features/applications/components/LanguageSkillsSection";
import { FileUploader } from "@/components/features/FileUploader";

import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { ProfileStepsLayout, Step } from "../components/ProfileStepsLayout";

interface ApplicationData {
  german_language_skills?: string;
  [key: string]: any;
}

interface TalentGermanSkillsProps {
  currentStep: number;
  totalSteps: number;
}

export default function TalentGermanSkills({
  currentStep,
  totalSteps
}: TalentGermanSkillsProps) {
  const { t } = useTranslation(["talent", "common"]);
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<ApplicationData>({});
  const [initialData, setInitialData] = useState<ApplicationData>({});

  // Step information
  const stepTitle = t("talent:dashboard.progress.steps.german_skills");
  const stepDescription = t(
    "talent:application.step_descriptions.german_skills"
  );
  const estimatedTime = "1-2 Monate";

  useEffect(() => {
    const fetchProfileData = async () => {
      if (profile.anonymous) return;

      try {
        const { data } = await supabase
          .from("talent_profiles")
          .select("*")
          .eq("id", profile.id)
          .single();

        if (data) {
          setFormData(data);
          setInitialData(data);
        }
      } catch (error) {
        console.error("Error fetching application data:", error);
      }
    };

    fetchProfileData();
  }, [profile]);

  const getStepStatus = (
    stepId: string
  ): "completed" | "current" | "upcoming" => {
    if (stepId === "german_skills") return "current";
    if (stepId === "experience" || stepId === "education") return "upcoming";
    return "completed";
  };

  const steps: Step[] = [
    {
      id: "personal_info",
      title: t("talent:layout.progress_steps.personal_info"),
      href: "/profile/personal-info",
      status: getStepStatus("personal_info")
    },
    {
      id: "german_skills",
      title: t("talent:dashboard.progress.steps.german_skills"),
      href: "/profile/german-skills",
      status: getStepStatus("german_skills")
    },
    {
      id: "experience",
      title: t("talent:dashboard.progress.steps.experience"),
      href: "/profile/experience",
      status: getStepStatus("experience")
    },
    {
      id: "education",
      title: t("talent:dashboard.progress.steps.education"),
      href: "/profile/education",
      status: getStepStatus("education")
    }
  ];

  const validateForm = () => {
    return Boolean(formData.german_language_skills);
  };

  const handleNext = async () => {
    if (!validateForm()) {
      toast({
        title: t("common:error"),
        description: "Bitte füllen Sie alle erforderlichen Felder aus",
        variant: "destructive"
      });
      return;
    }

    if (JSON.stringify(formData) === JSON.stringify(initialData)) {
      navigate("/profile/experience");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from("talent_profiles")
        .update({
          ...formData,
          updated_at: new Date().toISOString()
        })
        .eq("id", profile.id);

      if (error) throw error;

      toast({
        title: t("common:success"),
        description: "Deutsche Sprachkenntnisse erfolgreich gespeichert"
      });

      navigate("/profile/experience");
    } catch (error) {
      console.error("Error saving application:", error);
      toast({
        title: t("common:error"),
        description: "Fehler beim Speichern",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/profile/personal-info");
  };

  return (
    <ProfileStepsLayout
      currentStep={currentStep}
      totalSteps={totalSteps}
      title={stepTitle}
      description={stepDescription}
      estimatedTime={estimatedTime}
      steps={steps}
    >
      <div className="space-y-8">
        <FileUploader>
          <LanguageSkillsSection
            readOnly={false}
            loading={loading}
            profile={formData}
            setProfile={setFormData}
          />
        </FileUploader>
        {/* Navigation */}
        <div className="flex justify-between pt-6 border-t">
          <Button
            variant="outline"
            onClick={handleBack}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("common:buttons.back")}
          </Button>

          <Button
            id="next-button"
            onClick={handleNext}
            disabled={loading || !validateForm()}
            className="flex items-center gap-2"
          >
            {loading ? t("common:status.saving") : t("common:buttons.next")}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </ProfileStepsLayout>
  );
}
